import { useTranslation } from 'react-i18next';

/**
 * Active-locale resolution — the counterpart to {@link resolvePlantTimeZone}
 * for the *locale* (language + regional number/date conventions) rather than
 * the timezone.
 *
 * A bare `value.toLocaleString()` formats in whatever locale the operator's OS
 * happens to be set to, so a supervisor reviewing a German line from a US
 * laptop silently reads "1,234.5" where the record says "1.234,5". Every
 * number/date that isn't pinned to the plant timezone must therefore format
 * against an EXPLICIT locale — the tenant/user's resolved locale, not the
 * browser default.
 *
 * This module exposes:
 *   - {@link resolveActiveLocale} — pure, non-hook resolver usable from
 *     helper/formatter code that can't run a React hook. Reads the
 *     app-registered resolver (see {@link registerLocaleResolver}), then the
 *     browser's `navigator.language`, then `'en-US'`.
 *   - {@link useActiveLocale} — the hook form; prefers react-i18next's active
 *     language (so an in-session `i18n.changeLanguage` re-renders consumers)
 *     and falls back to {@link resolveActiveLocale}.
 *
 * The registration seam mirrors {@link registerPlantTimeZoneResolver}:
 * `@tyndo/hooks` stays free of any tenant/auth dependency, and callers that
 * never register a resolver still get correct (browser-local) behaviour. The
 * shell wires the resolver from its tenant/user locale context so imperative
 * formatter paths (`n.toLocaleString(resolveActiveLocale())`) format against
 * the SAME locale the React tree renders under.
 */

/** Zero-arg resolver returning the active tenant/user locale, or null. */
export type LocaleResolver = () => string | null | undefined;

let _localeResolver: LocaleResolver | null = null;

/**
 * Register the app's active-locale resolver. The shell calls this once (from
 * its tenant/user locale context) so every helper/formatter that can't run a
 * hook still resolves the SAME locale the React tree renders under. Passing
 * `null` clears the registration (used in tests).
 */
export function registerLocaleResolver(fn: LocaleResolver | null): void {
  _localeResolver = fn;
}

/** Browser-resolved BCP-47 locale, or `'en-US'` when unavailable. */
function browserLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
}

/**
 * Resolve the active tenant/user locale, outside React. Pure — usable from
 * imperative/formatter paths (`n.toLocaleString(resolveActiveLocale())`) that
 * can't run a hook. Falls back to the browser locale, then `'en-US'`.
 */
export function resolveActiveLocale(): string {
  const fromApp = _localeResolver?.();
  if (fromApp) return fromApp;
  return browserLocale();
}

/**
 * The active locale as a BCP-47 string (e.g. `'en-US'`). Prefers react-i18next's
 * current language so an in-session `i18n.changeLanguage` re-renders consumers;
 * falls back to {@link resolveActiveLocale} in trees that haven't initialised
 * i18n (unit tests / SSR).
 */
export function useActiveLocale(): string {
  const { i18n } = useTranslation();
  return i18n?.language || resolveActiveLocale();
}

/**
 * Pure locale-selection: pick the first candidate whose locale is supported,
 * else fall back to the first supported locale. A candidate matches a supported
 * locale exactly (`de-DE` ⇢ `de-DE`) or by its base language (`de` or `de-AT`
 * ⇢ `de-DE`) so a browser advertising a regional variant still resolves to the
 * bundle we actually ship. Nullish/blank candidates are skipped.
 *
 * Keeping this pure (no i18n/DOM access) makes the tenant→user→browser
 * precedence unit-testable and lets the shell clamp to the locales that
 * genuinely have resource bundles — never advertising a language with no
 * translations behind it.
 */
export function resolvePreferredLocale(
  candidates: ReadonlyArray<string | null | undefined>,
  supported: readonly string[],
): string {
  const fallback = supported[0] ?? 'en-US';
  if (supported.length === 0) return fallback;

  const base = (loc: string) => loc.split('-')[0]?.toLowerCase() ?? '';
  for (const candidate of candidates) {
    if (!candidate || !candidate.trim()) continue;
    const c = candidate.trim();
    // Exact (case-insensitive) match.
    const exact = supported.find((s) => s.toLowerCase() === c.toLowerCase());
    if (exact) return exact;
    // Base-language match (candidate `de-AT` ⇢ supported `de-DE`).
    const byBase = supported.find((s) => base(s) === base(c));
    if (byBase) return byBase;
  }
  return fallback;
}
